import { checklistValuesToDraft } from '@/services/checklist/checklistObservations';
import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, ActivityIndicator, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useNavigation } from 'expo-router';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '../../config/theme';
import { ChecklistActionsHelper } from '../../services/checklist/ChecklistActionsHelper';
import { showToast } from '../../utils/toast';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '@/config/constants';
import { useWebAlert } from '../WebAlert';
import { AppAppIndexLogger } from '@/utils/logger';

interface ChecklistCardProps {
  id: string;
  data_criacao: number;
  status: 'in_progress' | 'done';
  data_conclusao?: number;
  usuario_id?: string | null;
  templateName?: string;
  userName?: string;
  sequencialCodigo?: string | null;
  sequencialNumero?: number | null;
  compact?: boolean;
  onDeleted?: () => void;
}

/**
 * Componente card para exibir um checklist na listagem
 * Design minimalista conforme as instruções do projeto
 */
export const ChecklistCard: React.FC<ChecklistCardProps> = ({
  id,
  data_criacao,
  status,
  data_conclusao,
  usuario_id,
  templateName,
  userName,
  sequencialCodigo,
  sequencialNumero,
  compact = false,
  onDeleted,
}) => {
  const logger = AppAppIndexLogger.Checklist;
  const router = useRouter();
  const navigation = useNavigation<any>();
  const { showAlert } = useWebAlert();
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const handlePress = useCallback(() => {
    // Navegar para o detalhe do checklist
    router.push(`/(app)/checklist/${id}`);
  }, [id, router]);

  // Formatação de data
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return '--/--/-- - --:--';
    }

    const datePart = date.toLocaleDateString('pt-BR');
    const timePart = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${datePart} - ${timePart}`;
  };

  // Status em português
  const { statusText, statusColor } = useMemo(() => ({
    statusText: status === 'done' ? 'Concluído' : 'Em Andamento',
    statusColor: status === 'done' ? theme.colors.util.success : theme.colors.util.warning,
  }), [status]);

  const displayTemplateName = templateName?.trim() || 'Template não identificado';
  const displayUserName = (() => {
    if (userName?.trim()) {
      return userName.trim();
    }
    if (usuario_id?.length) {
      return `Usuário ${usuario_id.slice(-8)}`;
    }
    return 'Usuário não identificado';
  })();
  const templateAndUserLabel = `${displayTemplateName} - ${displayUserName}`;
  const sequentialValue = useMemo(() => {
    if (sequencialCodigo && sequencialCodigo.trim().length > 0) {
      return sequencialCodigo.trim();
    }
    if (typeof sequencialNumero === 'number' && Number.isFinite(sequencialNumero)) {
      return `Checklist #${sequencialNumero}`;
    }
    return null;
  }, [sequencialCodigo, sequencialNumero]);
  const sequentialText = sequentialValue || 'Aguardando numeração';
  const sequentialPending = !sequentialValue;

  const fetchChecklistContext = useCallback(async () => {
    const checklistRecord: any = await databaseService.readItem(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
      id
    );
    if (!checklistRecord) {
      throw new Error('Checklist não encontrado localmente');
    }

    let templateRecords: any[] = [];
    if (checklistRecord.template_id) {
      templateRecords = await databaseService.listItems(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
        { template_id: checklistRecord.template_id }
      );
      templateRecords.sort(
        (a, b) => Number(a?.ordem ?? 0) - Number(b?.ordem ?? 0)
      );
    }

    const valuesRecords = await databaseService.listItems<{ campo_id: string; valor: string | null; observacao?: string | null }>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
      { checklist_id: id }
    );
    const valuesMap = checklistValuesToDraft(valuesRecords);

    return {
      checklist: checklistRecord,
      template: templateRecords,
      values: valuesMap,
    };
  }, [id]);

  const handleGeneratePdf = useCallback(async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const context = await fetchChecklistContext();
      if (!context.template.length) {
        throw new Error('Template do checklist não disponível localmente');
      }

      let generatePdf: any;
      if (Platform.OS === 'web') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        ({ generateChecklistPdfWeb: generatePdf } = require('../../services/checklistPdfService.web'));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        ({ generateChecklistPdfNative: generatePdf } = require('../../services/checklistPdfService.native'));
      }

      if (!generatePdf) throw new Error('Gerador de PDF indisponível');

      await generatePdf({
        checklist: context.checklist,
        template: context.template,
        values: context.values,
        ...(Platform.OS === 'ios' && {
          onBeforePresentActions: async () => {
            setPdfBusy(false);
            navigation.getParent()?.setOptions({ tabBarStyle: { display: 'none' } });
            showToast('PDF pronto para salvar ou compartilhar.', {
              duration: 1800,
              type: 'info',
            });
            await new Promise((resolve) => setTimeout(resolve, 400));
          },
        }),
      });

      if (Platform.OS !== 'ios') {
        showToast('PDF gerado com sucesso!', { duration: 1200, type: 'success' });
      }
    } catch (error: any) {
      showToast(`Erro ao gerar PDF: ${error?.message || error}`, { duration: 2000, type: 'error' });
    } finally {
      if (Platform.OS === 'ios') {
        navigation.getParent()?.setOptions({ tabBarStyle: undefined });
      }
      setPdfBusy(false);
    }
  }, [fetchChecklistContext, navigation, pdfBusy]);

  const executeDelete = useCallback(async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    try {
      await ChecklistActionsHelper.deleteMany([id]);
      showToast('Checklist deletado com sucesso', { duration: 1500, type: 'success' });
      onDeleted?.();
    } catch (error: any) {
      showToast(`Erro ao deletar checklist: ${error?.message || error}`, { duration: 2000, type: 'error' });
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteBusy, id, onDeleted]);

  const confirmDelete = useCallback(() => {
    const message = 'Tem certeza que deseja deletar este checklist? Esta ação não pode ser desfeita.';
    if (Platform.OS !== 'android') {
      logger.info(`[deleteConfirm] os=${Platform.OS} origem=checklist_card apresentacao=themed_alert`);
      showAlert('Deletar checklist', message, [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Deletar',
          style: 'destructive',
          onPress: () => { void executeDelete(); },
        },
      ]);
    } else {
      logger.info('[deleteConfirm] os=android origem=checklist_card apresentacao=native_alert');
      Alert.alert(
        'Deletar checklist',
        message,
        [
          { text: 'Cancelar', style: 'cancel' },
          {
            text: 'Deletar',
            style: 'destructive',
            onPress: () => { void executeDelete(); },
          },
        ],
        { cancelable: true }
      );
    }
  }, [executeDelete, logger, showAlert]);

  return (
    <>
      <TouchableOpacity
        style={[styles.card, compact && styles.cardCompact]}
        onPress={handlePress}
        activeOpacity={0.7}
      >
        <View style={styles.headerRow}>
          <View style={styles.infoColumn}>
            <Text
              style={[styles.templateText, compact && styles.templateTextCompact]}
              numberOfLines={2}
            >
              {templateAndUserLabel}
            </Text>
            <View style={styles.content}>
              <Text
                style={[
                  styles.sequenceLabel,
                  compact && styles.sequenceLabelCompact,
                  sequentialPending && styles.sequenceLabelPending
                ]}
                numberOfLines={1}
              >
                Número: {sequentialText}
              </Text>
              <Text
                style={[styles.idLabel, compact && styles.idLabelCompact]}
                numberOfLines={1}
              >
                ID: #{id.slice(-6)}
              </Text>

              <Text style={[styles.dateLabel, compact && styles.dateLabelCompact]}>
                Criado em: {formatDate(data_criacao)}
              </Text>
              {data_conclusao && (
                <Text style={[styles.dateLabel, compact && styles.dateLabelCompact]}>
                  Concluído em: {formatDate(data_conclusao)}
                </Text>
              )}
            </View>
          </View>
          <View style={styles.actionsColumn}>
            <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
              <Text style={[styles.statusText, compact && styles.statusTextCompact]}>
                {statusText}
              </Text>
            </View>
            <View style={styles.actionsRow}>
              <TouchableOpacity
                style={[styles.actionButton, deleteBusy && styles.actionDisabled]}
                onPress={(event) => {
                  event.stopPropagation();
                  confirmDelete();
                }}
                disabled={deleteBusy}
                accessibilityLabel="Excluir checklist"
              >
                {deleteBusy ? (
                  <ActivityIndicator size="small" color={theme.colors.util.error} />
                ) : (
                  <Ionicons name="trash" size={20} color={theme.colors.util.error} />
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, pdfBusy && styles.actionDisabled]}
                onPress={(event) => {
                  event.stopPropagation();
                  void handleGeneratePdf();
                }}
                disabled={pdfBusy}
                accessibilityLabel={`Gerar PDF do checklist ${status === 'done' ? 'concluído' : 'em andamento'}`}
              >
                {pdfBusy ? (
                  <ActivityIndicator size="small" color={theme.colors.primary.default} />
                ) : (
                  <MaterialCommunityIcons name="file-pdf-box" size={22} color={theme.colors.secondary.dark} />
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(event) => {
                  event.stopPropagation();
                  handlePress();
                }}
                accessibilityLabel="Abrir detalhes do checklist"
              >
                <Ionicons name="chevron-forward" size={20} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>

    </>
  );
};

const styles = StyleSheet.create({
  /**
   * Card principal do checklist
   * - Fundo: cor de card do tema
   * - Borda: sutil, cor do tema
   * - Sombra: multiplataforma, do tema
   * - Cantos arredondados: do tema
   */
  card: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    padding: 16,
    marginVertical: 4,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    ...theme.shadows.sm,
  },
  /**
   * Versão compacta do card
   * - Menor padding e sombra mais leve
   */
  cardCompact: {
    padding: 12,
    marginVertical: 2,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
    ...theme.shadows.sm,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  statusTextCompact: {
    fontSize: 10,
  },
  sequenceLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#4B5563',
  },
  sequenceLabelCompact: {
    fontSize: 13,
  },
  sequenceLabelPending: {
    color: theme.colors.text.secondary,
    fontStyle: 'italic',
  },
  content: {
    gap: 8,
  },
  templateText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: '#1F2933',
  },
  templateTextCompact: {
    fontSize: 14,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: theme.spacing.md,
  },
  infoColumn: {
    flex: 1,
  },
  idLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#4B5563',
  },
  idLabelCompact: {
    fontSize: 12,
  },
  dateLabel: {
    fontSize: 14,
    color: '#666666',
  },
  dateLabelCompact: {
    fontSize: 12,
  },
  actionsColumn: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing.sm,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: theme.borderRadius.circular,
    backgroundColor: theme.colors.background.tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    ...theme.shadows.sm,
  },
  actionDisabled: {
    opacity: 0.6,
  },
});
